import moment from "moment";
import { LuLandmark, LuUsers } from "react-icons/lu";
import { HiOutlineLocationMarker } from "react-icons/hi";
import TitleStripe from "../TitleStripe";
import { formatDocument, formatToReal, maskCEP } from "../../../utils/utils";

const formatData = (valor) => {
  if (!valor) return null;
  const data = moment(valor, ["YYYY-MM-DD", "DD/MM/YYYY", "YYYYMMDD"], true);
  return data.isValid() ? data.format("DD/MM/YYYY") : valor;
};

// "5611201" -> "5611-2/01"
const formatCNAE = (codigo) => {
  const digitos = String(codigo || "").replace(/\D/g, "");
  if (digitos.length !== 7) return codigo || null;
  return digitos.replace(/^(\d{4})(\d)(\d{2})$/, "$1-$2/$3");
};

// A Receita devolve o CPF do sócio mascarado ("***249021**"); formatar
// apagaria os asteriscos e inventaria dígitos.
const formatDocumentoSocio = (documento) =>
  /^(\d{11}|\d{14})$/.test(documento || "")
    ? formatDocument(documento)
    : documento || "—";

// Com a opção falsa a data não é de adesão, então ela só aparece quando é "sim".
const formatOpcao = (opcao, data, sim, nao) => ({
  value: opcao ? sim : nao,
  hint: opcao && formatData(data) ? `Desde ${formatData(data)}` : null,
});

const InfoTile = ({ label, value, hint, valueClassName = "text-gray-900" }) => (
  <div className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2">
    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
      {label}
    </p>
    <p className={`text-base font-semibold ${valueClassName}`}>
      {value || "Não informado"}
    </p>
    {hint && <p className="text-[10px] text-gray-500">{hint}</p>}
  </div>
);

const DadosReceitaFederalPJ = ({ receita }) => {
  const situacao = receita?.situacaoCadastral || receita?.situacao;
  const situacaoAtiva = /^ativ/i.test(situacao || "");
  const simples = formatOpcao(
    receita?.opcaoSimples,
    receita?.dataOpcaoSimples,
    "Optante",
    "Não optante"
  );
  const mei = formatOpcao(
    receita?.opcaoMei,
    receita?.dataOpcaoMei,
    "Sim",
    "Não"
  );
  const cnaesSecundarios = (receita?.cnaesSecundarios || [])
    .map(formatCNAE)
    .filter(Boolean);
  const telefones = (receita?.telefones || [])
    .map((telefone) => {
      const numero =
        telefone?.formatado ||
        [telefone?.ddd, telefone?.numero].filter(Boolean).join(" ");
      return numero && telefone?.isFax ? `${numero} (fax)` : numero;
    })
    .filter(Boolean);
  const socios = receita?.socios || [];
  const endereco = [
    receita?.logradouro
      ? [receita.logradouro, receita.numero].filter(Boolean).join(", ")
      : receita?.endereco,
    receita?.bairro,
    [receita?.municipio || receita?.cidade, receita?.uf]
      .filter(Boolean)
      .join("/"),
    receita?.cep && `CEP ${maskCEP(receita.cep)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div>
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <LuLandmark className="text-blue-700" />
          <span>Receita Federal</span>
        </div>
      </TitleStripe>

      <div className="grid gap-2 grid-cols-3 text-xs">
        <InfoTile
          label="Situação cadastral"
          value={situacao}
          hint={
            formatData(receita?.dataSituacaoCadastral) &&
            `Desde ${formatData(receita?.dataSituacaoCadastral)}`
          }
          valueClassName={
            situacao && !situacaoAtiva ? "text-red-600" : "text-gray-900"
          }
        />
        <InfoTile
          label="Porte"
          value={receita?.porteEmpresa}
          hint={receita?.matrizFilial}
        />
        <InfoTile
          label="Capital social"
          value={
            receita?.capitalSocial != null
              ? formatToReal(receita.capitalSocial)
              : null
          }
        />
        <InfoTile
          label="Início da atividade"
          value={formatData(
            receita?.dataInicioAtividade || receita?.dataFundacao
          )}
        />
        <InfoTile
          label="Simples Nacional"
          value={simples.value}
          hint={simples.hint}
        />
        <InfoTile label="MEI" value={mei.value} hint={mei.hint} />
      </div>

      <div className="mt-2 grid gap-2 grid-cols-2 text-xs">
        <InfoTile
          label="CNAE principal"
          value={formatCNAE(receita?.cnaePrincipal)}
        />
        <div className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
            CNAEs secundários
          </p>
          {cnaesSecundarios.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {cnaesSecundarios.map((cnae) => (
                <span
                  key={cnae}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-700"
                >
                  {cnae}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-base font-semibold text-gray-900">Nenhum</p>
          )}
        </div>
        <InfoTile label="E-mail" value={receita?.email} />
        <InfoTile label="Telefones" value={telefones.join(" · ")} />
      </div>

      {endereco && (
        <div className="mt-2 flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2">
          <HiOutlineLocationMarker className="h-4 w-4 shrink-0 text-gray-500" />
          <p className="text-[12px] font-semibold text-gray-900">{endereco}</p>
        </div>
      )}

      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <LuUsers className="text-blue-700" />
          <span>Quadro societário</span>
        </div>
      </TitleStripe>

      {socios.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <div className="grid grid-cols-4 gap-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
              <div className="col-span-2">Nome</div>
              <div>Qualificação</div>
              <div>CPF/CNPJ</div>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {socios.map((socio, index) => (
              <div
                key={index}
                className={`grid grid-cols-4 items-center gap-3 px-4 py-3 text-sm ${
                  index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                }`}
              >
                <div className="col-span-2 font-medium text-slate-900">
                  {socio?.nome || "—"}
                </div>
                <div className="text-slate-700">{socio?.cargo || "—"}</div>
                <div className="text-slate-700 tabular-nums">
                  {formatDocumentoSocio(socio?.cpf || socio?.cnpj)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-3 text-sm text-gray-600">
          Nenhum sócio informado pela Receita.
        </div>
      )}
    </div>
  );
};

export default DadosReceitaFederalPJ;
